import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, CalendarClock } from "lucide-react";

// Launch countdown to Open Studio opening day (first session at 2:00 PM).
// Auto-hides once the opening moment has passed.
export default function OpenStudioCountdown({ openingDate, showCta = true }) {
  const [iso, setIso] = useState(openingDate || null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (openingDate) { setIso(openingDate); return; }
    api.get("/open-studio/info").then(({ data }) => setIso(data?.opening_date || null)).catch(() => {});
  }, [openingDate]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const target = new Date(y, m - 1, d, 12, 0, 0).getTime(); // first session: 12:00 PM
  const diff = target - now;
  const openLabel = new Date(y, m - 1, d).toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric" });

  // Open Studio is live — switch the banner to an evergreen "now open" message.
  if (diff <= 0) {
    return (
      <div data-testid="open-studio-open-banner" className="bg-[#121212] text-[#F2E9D8] border-2 border-[#121212]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-bold text-[#C6FF33]">
              <CalendarClock size={15} /> Now open
            </p>
            <p className="font-serif font-black text-2xl sm:text-3xl mt-1 leading-tight">
              Open Studio is now open — reserve your table.
            </p>
            <p className="text-sm text-[#F2E9D8]/70 mt-0.5">Every Wednesday, Thursday &amp; Friday. Pick your time, choose an experience, and we'll have everything ready.</p>
          </div>
          {showCta && (
            <Link to="/open-studio" data-testid="open-banner-cta" className="inline-flex items-center gap-2 px-6 py-3 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#b3ec1f] transition-colors whitespace-nowrap self-start md:self-auto">
              Reserve your table <ArrowRight size={15} />
            </Link>
          )}
        </div>
      </div>
    );
  }

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const tiles = [["Days", days], ["Hrs", hours], ["Min", mins], ["Sec", secs]];

  return (
    <div data-testid="open-studio-countdown" className="bg-[#121212] text-[#F2E9D8] border-2 border-[#121212]">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div>
          <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-bold text-[#C6FF33]">
            <CalendarClock size={15} /> Now reserving
          </p>
          <p className="font-serif font-black text-2xl sm:text-3xl mt-1 leading-tight">
            Open Studio opens {openLabel}.
          </p>
          <p className="text-sm text-[#F2E9D8]/70 mt-0.5">First tables start at 12:00 PM — grab yours before they fill up.</p>
        </div>
        <div className="flex items-center gap-5">
          <div className="flex gap-2">
            {tiles.map(([label, val]) => (
              <div key={label} data-testid={`countdown-${label.toLowerCase()}`} className="w-14 text-center bg-[#F2E9D8] text-[#121212] py-2">
                <div className="font-serif font-black text-2xl leading-none tabular-nums">{String(val).padStart(2, "0")}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase font-bold mt-1 text-[#121212]/60">{label}</div>
              </div>
            ))}
          </div>
          {showCta && (
            <Link to="/open-studio" data-testid="countdown-cta" className="hidden sm:inline-flex items-center gap-2 px-6 py-3 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#b3ec1f] transition-colors whitespace-nowrap">
              Book now <ArrowRight size={15} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
